import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Bell, MonitorPlay, History, ChevronLeft, Search, X, Check,
  Users, Image as ImageIcon, Link as LinkIcon, Loader, CheckCircle2,
  Eye, MousePointerClick, ExternalLink, UserCheck, ChevronDown, RotateCcw,
  Link2, Trash2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import adminApi from '../../services/adminApi';
import { request, getApiBaseUrl } from '../../services/cloudApi';
import { useAuth } from '../../contexts/AuthContext';
import { formatScheduleSummary, getDefaultScheduleDate } from '../../utils/adminNotificationSchedule';


/* ─── Yardımcı: tarih formatla ─────────────────────────────── */
const formatDate = (iso, locale = 'tr') => {
  const d = new Date(iso);
  return d.toLocaleString(locale, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/* ─── Küçük modal bileşeni ─────────────────────────────────── */
const MiniModal = ({ title, onClose, children }) => (
  <>
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000]" onClick={onClose} />
    <div className="fixed inset-0 flex items-center justify-center z-[10001] p-4">
      <div className="bg-[#0d1424] border border-white/[0.08] rounded-2xl shadow-2xl w-full max-w-sm max-h-[80vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
          <h3 className="text-sm font-semibold text-white">{title}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/[0.05] text-slate-500 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 p-4">{children}</div>
      </div>
    </div>
  </>
);

/* ─── Geçmiş bildirim kartı ────────────────────────────────── */
const HistoryCard = ({ item, onResend }) => {
  const { t, i18n } = useTranslation('admin');
  const [modal, setModal] = useState(null); // 'recipients' | 'image' | 'seen' | 'clicked' | 'deleted'
  const [expanded, setExpanded] = useState(false);
  const [resending, setResending] = useState(false);

  const handleResend = async (e) => {
    e.stopPropagation();
    if (resending) return;
    setResending(true);
    try {
      await onResend(item);
    } finally {
      setResending(false);
    }
  };

  const isModal = item.type === 'modal';
  const typeLabel = isModal ? t('adminNotifications.typeLabels.modal') : t('adminNotifications.typeLabels.bell');
  const typeBg = isModal ? 'bg-blue-500/10 text-blue-400' : 'bg-amber-500/10 text-amber-400';
  const TypeIcon = isModal ? MonitorPlay : Bell;

  return (
    <>
      <div className="bg-[#0d1424] border border-white/[0.07] rounded-xl overflow-hidden hover:border-white/[0.12] transition-colors">
        {/* Üst şerit — her zaman görünür, tıklanınca açılır/kapanır */}
        <button
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-white/[0.02] transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-semibold ${typeBg}`}>
              <TypeIcon className="w-3 h-3" />
              {typeLabel}
            </span>
            {item.isResend && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-orange-500/10 text-orange-400">
                <RotateCcw className="w-2.5 h-2.5" />
                {t('adminNotifications.resendBadge')}
              </span>
            )}
            <span className="text-xs text-slate-600">{formatDate(item.sentAt, i18n.language)}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleResend}
              disabled={resending}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-white/[0.04] hover:bg-white/[0.08] text-slate-400 hover:text-slate-200 border border-white/[0.06] transition-all disabled:opacity-50"
            >
              {resending
                ? <Loader className="w-3 h-3 animate-spin" />
                : <RotateCcw className="w-3 h-3" />
              }
              {resending ? t('adminNotifications.resending') : t('adminNotifications.resendButton')}
            </button>
            <ChevronDown className={`w-4 h-4 text-slate-600 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
          </div>
        </button>

        {/* Genişletilmiş içerik */}
        {expanded && (
          <>
            <div className="border-t border-white/[0.05]" />
            {/* İçerik */}
            <div className="px-5 py-4 grid grid-cols-2 gap-x-8 gap-y-3">
          {/* Gönderen */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-0.5">{t('adminNotifications.fields.sender')}</p>
            <p className="text-sm font-medium text-slate-300">{item.sentBy}</p>
          </div>

          {/* Gönderilen */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-0.5">{t('adminNotifications.fields.recipients')}</p>
            {item.recipientType === 'all' ? (
              <p className="text-sm font-medium text-slate-300">{t('adminNotifications.fields.everyone')}</p>
            ) : (
              <button
                onClick={() => setModal('recipients')}
                className="text-sm font-medium text-blue-400 hover:text-blue-300 underline underline-offset-2 transition-colors"
              >
                {t('adminNotifications.fields.customCount', { count: item.recipients.length })}
              </button>
            )}
          </div>

          {/* Fotoğraf */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-0.5">{t('adminNotifications.fields.photo')}</p>
            {item.imageUrl ? (
              <button
                onClick={() => setModal('image')}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-400 hover:text-emerald-300 transition-colors"
              >
                <ImageIcon className="w-3.5 h-3.5" />
                {t('adminNotifications.fields.viewPhoto')}
              </button>
            ) : (
              <p className="text-sm text-slate-600">—</p>
            )}
          </div>

          {/* Link */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-0.5">{t('adminNotifications.fields.link')}</p>
            {item.type === 'bell' ? (
              // Çan bildirimi: link içerikte <a> olarak gömülü
              (() => {
                const match = item.content && item.content.match(/href=["']([^"']+)["']/);
                const href = match ? match[1] : null;
                const url = href ? (/^https?:\/\//i.test(href) ? href : `https://${href}`) : null;
                return url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-purple-400 hover:text-purple-300 transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    {t('adminNotifications.fields.linkAvailable')}
                  </a>
                ) : (
                  <p className="text-sm text-slate-600">{t('adminNotifications.fields.linkNotAdded')}</p>
                );
              })()
            ) : item.link ? (
              // Modal bildirimi: ayrı link alanı
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-purple-400 hover:text-purple-300 transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                {t('adminNotifications.fields.viewLink')}
              </a>
            ) : (
              <p className="text-sm text-slate-600">—</p>
            )}
          </div>

          {isModal && (
            <div className="col-span-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-0.5">{t('adminNotifications.fields.schedule')}</p>
              <p className="text-sm text-slate-300">{formatScheduleSummary(item)}</p>
            </div>
          )}
        </div>

            {/* Alt istatistik şeridi */}
            <div className="flex border-t border-white/[0.05]">
              {/* Görülme */}
              <button
                onClick={() => setModal('seen')}
                className="flex-1 flex items-center justify-center gap-2 py-3 hover:bg-white/[0.03] transition-colors group"
              >
                <Eye className="w-4 h-4 text-slate-500 group-hover:text-emerald-400 transition-colors" />
                <span className="text-sm font-semibold text-slate-300">{item.seenCount}</span>
                <span className="text-xs text-slate-600">{t('adminNotifications.stats.seenSuffix', { total: item.totalRecipients })}</span>
              </button>

              {(item.type === 'bell' ? (item.content && item.content.includes('<a ')) : item.link) && <div className="w-px bg-white/[0.05]" />}
              {(item.type === 'bell' ? (item.content && item.content.includes('<a ')) : item.link) && (
                /* Tıklama */
                <button
                  onClick={() => setModal('clicked')}
                  className="flex-1 flex items-center justify-center gap-2 py-3 hover:bg-white/[0.03] transition-colors group"
                >
                  <MousePointerClick className="w-4 h-4 text-slate-500 group-hover:text-blue-400 transition-colors" />
                  <span className="text-sm font-semibold text-slate-300">{item.clickCount}</span>
                  <span className="text-xs text-slate-600">{t('adminNotifications.stats.clickedSuffix', { total: item.totalRecipients })}</span>
                </button>
              )}

              {item.type === 'bell' && <div className="w-px bg-white/[0.05]" />}
              {item.type === 'bell' && (
                /* Silme — sadece çan bildirimleri */
                <button
                  onClick={() => setModal('deleted')}
                  className="flex-1 flex items-center justify-center gap-2 py-3 hover:bg-white/[0.03] transition-colors group"
                >
                  <Trash2 className="w-4 h-4 text-slate-500 group-hover:text-red-400 transition-colors" />
                  <span className="text-sm font-semibold text-slate-300">{item.deletedCount ?? 0}</span>
                  <span className="text-xs text-slate-600">{t('adminNotifications.stats.deletedSuffix', { total: item.totalRecipients })}</span>
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── Alıcı modalı ── */}
      {modal === 'recipients' && (
        <MiniModal title={t('adminNotifications.modals.recipientsTitle')} onClose={() => setModal(null)}>
          <div className="space-y-2">
            {item.recipients.map((u) => (
              <div key={u.id} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white/[0.03]">
                <div className="w-7 h-7 rounded-full bg-[#1e2d47] flex items-center justify-center text-xs font-semibold text-blue-300 flex-shrink-0">
                  {(u.name || '?').charAt(0).toUpperCase()}
                </div>
                <span className="text-sm text-slate-300">{u.name}</span>
              </div>
            ))}
          </div>
        </MiniModal>
      )}

      {/* ── Fotoğraf modalı ── */}
      {modal === 'image' && item.imageUrl && (
        <MiniModal title={t('adminNotifications.modals.photoTitle')} onClose={() => setModal(null)}>
          <img src={item.imageUrl} alt={t('adminNotifications.modals.photoAlt')} className="w-full rounded-xl object-contain" />
        </MiniModal>
      )}

      {/* ── Görülme modalı ── */}
      {modal === 'seen' && (
        <MiniModal title={t('adminNotifications.modals.seenTitle', { seen: item.seenCount, total: item.totalRecipients })} onClose={() => setModal(null)}>
          <div className="space-y-1.5">
            {item.seenUsers.map((u) => (
              <div key={u.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.03]">
                <div className="w-7 h-7 rounded-full bg-[#1e2d47] flex items-center justify-center text-xs font-semibold text-blue-300 flex-shrink-0">
                  {u.name.charAt(0).toUpperCase()}
                </div>
                <span className="text-sm text-slate-300 flex-1">{u.name}</span>
                {u.seen ? (
                  <span className="flex items-center gap-1 text-xs font-medium text-emerald-400">
                    <Check className="w-3.5 h-3.5" /> {t('adminNotifications.modals.saw')}
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs font-medium text-red-400">
                    <X className="w-3.5 h-3.5" /> {t('adminNotifications.modals.didNotSee')}
                  </span>
                )}
              </div>
            ))}
          </div>
        </MiniModal>
      )}

      {/* ── Silme modalı ── */}
      {modal === 'deleted' && (
        <MiniModal title={t('adminNotifications.modals.deletedTitle', { deleted: item.deletedCount ?? 0, total: item.totalRecipients })} onClose={() => setModal(null)}>
          <div className="space-y-1.5">
            {item.seenUsers.map((u) => (
              <div key={u.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.03]">
                <div className="w-7 h-7 rounded-full bg-[#1e2d47] flex items-center justify-center text-xs font-semibold text-blue-300 flex-shrink-0">
                  {u.name.charAt(0).toUpperCase()}
                </div>
                <span className="text-sm text-slate-300 flex-1">{u.name}</span>
                {u.deleted ? (
                  <span className="flex items-center gap-1 text-xs font-medium text-red-400">
                    <Trash2 className="w-3.5 h-3.5" /> {t('adminNotifications.modals.deleted')}
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs font-medium text-slate-600">
                    <Check className="w-3.5 h-3.5" /> {t('adminNotifications.modals.notDeleted')}
                  </span>
                )}
              </div>
            ))}
          </div>
        </MiniModal>
      )}

      {/* ── Tıklama modalı ── */}
      {modal === 'clicked' && (
        <MiniModal title={t('adminNotifications.modals.clickedTitle', { clicked: item.clickCount, total: item.totalRecipients })} onClose={() => setModal(null)}>
          <div className="space-y-1.5">
            {item.seenUsers.map((u) => (
              <div key={u.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.03]">
                <div className="w-7 h-7 rounded-full bg-[#1e2d47] flex items-center justify-center text-xs font-semibold text-blue-300 flex-shrink-0">
                  {u.name.charAt(0).toUpperCase()}
                </div>
                <span className="text-sm text-slate-300 flex-1">{u.name}</span>
                {u.clicked ? (
                  <span className="flex items-center gap-1 text-xs font-medium text-emerald-400">
                    <Check className="w-3.5 h-3.5" /> {t('adminNotifications.modals.clicked')}
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs font-medium text-red-400">
                    <X className="w-3.5 h-3.5" /> {t('adminNotifications.modals.didNotClick')}
                  </span>
                )}
              </div>
            ))}
          </div>
        </MiniModal>
      )}
    </>
  );
};

/* ─── Geçmiş Bildirimler ekranı ────────────────────────────── */
const NotificationHistory = ({ onBack }) => {
  const { t } = useTranslation('admin');
  const { authToken, user } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const authTokenRef = useRef(authToken);
  authTokenRef.current = authToken;

  const loadHistory = useCallback(async () => {
    const base = await getApiBaseUrl();
    const serverBase = base.replace('/api', '');
    const res = await request('/admin/notifications/history', {
      method: 'GET',
      token: authTokenRef.current,
    });
    return (res.notifications || []).map((n) => ({
      ...n,
      imageUrl: n.imageUrl && !n.imageUrl.startsWith('http')
        ? `${serverBase}${n.imageUrl}`
        : n.imageUrl,
    }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const load = async () => {
      try {
        const items = await loadHistory();
        setHistory(items);
      } catch {
        setHistory([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [loadHistory]);

  // Anlık istatistik güncellemesi: kullanıcı seen/clicked/deleted yaptığında
  useEffect(() => {
    const handler = async () => {
      try {
        const items = await loadHistory();
        setHistory(items);
      } catch (_) {}
    };
    window.addEventListener('notesplus-admin-notif-stats', handler);
    return () => window.removeEventListener('notesplus-admin-notif-stats', handler);
  }, [loadHistory]);

  const handleResend = async (item) => {
    const hasSchedule = Boolean(item.showStartAt);
    await request('/admin/notifications/send', {
      method: 'POST',
      token: authToken,
      body: {
        type: item.type,
        recipientType: item.recipientType,
        recipientIds: item.recipientType === 'special' ? item.recipients.map((r) => r.id) : [],
        imageUrl: item.imageUrl || null,
        link: item.link || null,
        content: item.content || null,
        isResend: true,
        scheduleEnabled: hasSchedule,
        showDate: hasSchedule ? new Date(item.showStartAt).toISOString().slice(0, 10) : null,
        windowStartTime: item.windowStartTime || null,
        windowEndTime: item.windowEndTime || null,
        durationDays: item.durationDays ?? 0,
        durationHours: item.durationHours ?? 0,
      },
    });
    // Geçmişi yenile
    try {
      const items = await loadHistory();
      setHistory(items);
    } catch { /* sessizce geç */ }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-8">
        <button onClick={onBack} className="p-2 rounded-xl hover:bg-white/[0.05] text-slate-400 transition-colors">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="w-9 h-9 rounded-xl bg-purple-500/10 flex items-center justify-center">
          <History className="w-5 h-5 text-purple-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">{t('adminNotifications.history.title')}</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('adminNotifications.history.subtitle')}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm py-8">
          <Loader className="w-4 h-4 animate-spin" />
          <span>{t('adminNotifications.history.loading')}</span>
        </div>
      ) : history.length === 0 ? (
        <div className="text-center py-16 text-slate-600">
          <History className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">{t('adminNotifications.history.empty')}</p>
        </div>
      ) : (
        <div className="space-y-4 max-w-2xl">
          {history.map((item) => (
            <HistoryCard key={item.id} item={item} onResend={handleResend} />
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Kullanıcı seçici ─────────────────────────────────────── */
const UserSelector = ({ users, selected, onChange }) => {
  const { t } = useTranslation('admin');
  const [search, setSearch] = useState('');
  const allSelected = selected === 'all';

  const standardUsers = users.filter((u) => u.role !== 'admin' && u.role !== 'plus');
  const standardIds = standardUsers.map((u) => u.id).sort();
  const currentIds = Array.isArray(selected) ? [...selected].sort() : null;
  const standardSelected =
    currentIds !== null &&
    currentIds.length === standardIds.length &&
    currentIds.every((id, i) => id === standardIds[i]);

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  const toggleUser = (id) => {
    if (allSelected) {
      onChange(users.map((u) => u.id).filter((uid) => uid !== id));
      return;
    }
    const arr = Array.isArray(selected) ? selected : [];
    onChange(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  };

  const isChecked = (id) => allSelected || (Array.isArray(selected) && selected.includes(id));

  return (
    <div className="border border-white/[0.08] rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => onChange(allSelected ? [] : 'all')}
        className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors border-b border-white/[0.06] ${
          allSelected ? 'bg-blue-500/10 text-blue-400' : 'hover:bg-white/[0.03] text-slate-300'
        }`}
      >
        <div className={`w-5 h-5 rounded flex items-center justify-center border ${allSelected ? 'bg-blue-500 border-blue-500' : 'border-white/20'}`}>
          {allSelected && <Check className="w-3 h-3 text-white" />}
        </div>
        <Users className="w-4 h-4" />
        <span className="font-medium">{t('adminNotifications.userSelector.selectEveryone')}</span>
        <span className="ml-auto text-xs text-slate-500">{t('adminNotifications.userSelector.userCountSuffix', { count: users.length })}</span>
      </button>
      <button
        type="button"
        onClick={() => onChange(standardSelected ? [] : standardIds)}
        className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors border-b border-white/[0.06] ${
          standardSelected ? 'bg-blue-500/10 text-blue-400' : 'hover:bg-white/[0.03] text-slate-300'
        }`}
      >
        <div className={`w-5 h-5 rounded flex items-center justify-center border ${standardSelected ? 'bg-blue-500 border-blue-500' : 'border-white/20'}`}>
          {standardSelected && <Check className="w-3 h-3 text-white" />}
        </div>
        <Users className="w-4 h-4" />
        <span className="font-medium">{t('adminNotifications.userSelector.standardOnly')}</span>
        <span className="ml-auto text-xs text-slate-500">{t('adminNotifications.userSelector.userCountSuffix', { count: standardUsers.length })}</span>
      </button>
      <div className="relative border-b border-white/[0.06]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input
          type="text"
          placeholder={t('adminNotifications.userSelector.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-transparent pl-9 pr-4 py-2.5 text-sm text-slate-300 placeholder-slate-600 focus:outline-none"
        />
      </div>
      <div className="max-h-52 overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="text-center text-xs text-slate-600 py-6">{t('adminNotifications.userSelector.noUsersFound')}</p>
        ) : (
          filtered.map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => toggleUser(u.id)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                isChecked(u.id) ? 'bg-blue-500/10 text-blue-300' : 'hover:bg-white/[0.03] text-slate-400'
              }`}
            >
              <div className={`w-5 h-5 rounded flex items-center justify-center border flex-shrink-0 ${isChecked(u.id) ? 'bg-blue-500 border-blue-500' : 'border-white/20'}`}>
                {isChecked(u.id) && <Check className="w-3 h-3 text-white" />}
              </div>
              <div className="w-7 h-7 rounded-full bg-[#1e2d47] flex items-center justify-center text-xs font-semibold text-blue-300 flex-shrink-0">
                {(u.name || u.email || '?').charAt(0).toUpperCase()}
              </div>
              <div className="text-left min-w-0">
                <p className="truncate font-medium text-xs">{u.name || u.email}</p>
                {u.name && <p className="truncate text-[11px] text-slate-600">{u.email}</p>}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

/* ─── Modal Bildirimi Formu ────────────────────────────────── */
const ModalNotificationForm = ({ onBack }) => {
  const { t } = useTranslation('admin');
  const { authToken, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selected, setSelected] = useState('all');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [link, setLink] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [showDate, setShowDate] = useState(getDefaultScheduleDate);
  const [windowStartTime, setWindowStartTime] = useState('09:00');
  const [windowEndTime, setWindowEndTime] = useState('18:00');
  const [durationDays, setDurationDays] = useState(0);
  const [durationHours, setDurationHours] = useState(24);
  const fileRef = useRef();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await adminApi.getUsers({ token: authToken, userId: user?.id });
        setUsers(res.users || []);
      } catch {
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    load();
  }, [authToken, user]);

  const handleImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleClear = () => {
    setSelected('all');
    setImage(null);
    setImagePreview(null);
    setLink('');
    setScheduleEnabled(false);
    setShowDate(getDefaultScheduleDate());
    setWindowStartTime('09:00');
    setWindowEndTime('18:00');
    setDurationDays(0);
    setDurationHours(24);
    if (fileRef.current) fileRef.current.value = '';
  };

  const selectedCount = selected === 'all' ? users.length : (Array.isArray(selected) ? selected.length : 0);

  const handleSend = async () => {
    if (selectedCount === 0) return;
    if (scheduleEnabled && durationDays === 0 && durationHours === 0) {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'error', title: t('adminNotifications.toasts.scheduleTitle'), message: t('adminNotifications.toasts.scheduleDurationRequired') },
      }));
      return;
    }
    setSending(true);
    try {
      let uploadedImageUrl = null;

      // Fotoğraf varsa önce yükle
      if (image) {
        const apiBase = await getApiBaseUrl();
        const formData = new FormData();
        formData.append('image', image);
        const uploadRes = await fetch(`${apiBase}/admin/notifications/upload-image`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${authToken}` },
          body: formData,
        });
        const uploadData = await uploadRes.json();
        if (!uploadData.ok) throw new Error(t('adminNotifications.toasts.imageUploadFailed'));
        // Tam URL'ye çevir (/uploads/... → https://api.../uploads/...)
        const relUrl = uploadData.imageUrl;
        const baseUrl = apiBase.replace('/api', '');
        uploadedImageUrl = relUrl.startsWith('http') ? relUrl : `${baseUrl}${relUrl}`;
      }

      // Bildirimi gönder
      const recipientIds = selected === 'all' ? null : selected;
      await request('/admin/notifications/send', {
        method: 'POST',
        token: authToken,
        body: {
          type: 'modal',
          recipientType: selected === 'all' ? 'all' : 'special',
          recipientIds,
          imageUrl: uploadedImageUrl,
          link: link || null,
          scheduleEnabled,
          showDate: scheduleEnabled ? showDate : null,
          windowStartTime: scheduleEnabled ? windowStartTime : null,
          windowEndTime: scheduleEnabled ? windowEndTime : null,
          durationDays: scheduleEnabled ? durationDays : 0,
          durationHours: scheduleEnabled ? durationHours : 0,
        },
      });

      setSent(true);
      setTimeout(() => setSent(false), 3000);
      handleClear();
    } catch (err) {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'error', title: t('adminNotifications.toasts.errorTitle'), message: t('adminNotifications.toasts.sendFailed') },
      }));
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-8">
        <button onClick={onBack} className="p-2 rounded-xl hover:bg-white/[0.05] text-slate-400 transition-colors">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="w-9 h-9 rounded-xl bg-blue-500/10 flex items-center justify-center">
          <MonitorPlay className="w-5 h-5 text-blue-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">{t('adminNotifications.modalForm.title')}</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('adminNotifications.modalForm.subtitle')}</p>
        </div>
      </div>

      <div className="space-y-6 max-w-2xl">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {t('adminNotifications.modalForm.recipientsLabel')}
          </label>
          {loadingUsers ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm py-4">
              <Loader className="w-4 h-4 animate-spin" />
              <span>{t('adminNotifications.modalForm.loadingUsers')}</span>
            </div>
          ) : (
            <UserSelector users={users} selected={selected} onChange={setSelected} />
          )}
          {selectedCount > 0 && (
            <p className="text-xs text-blue-400 mt-2">{t('adminNotifications.modalForm.selectedCount', { count: selectedCount })}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {t('adminNotifications.modalForm.photoLabel')}
            <span className="ml-2 text-xs text-slate-500 font-normal">{t('adminNotifications.modalForm.photoRecommended')}</span>
          </label>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleImage} className="hidden" />
          {imagePreview ? (
            <div className="relative inline-block">
              <img src={imagePreview} alt={t('adminNotifications.modalForm.preview')} className="w-40 h-40 object-cover rounded-xl border border-white/[0.08]" />
              <button
                type="button"
                onClick={() => { setImage(null); setImagePreview(null); if (fileRef.current) fileRef.current.value = ''; }}
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 flex items-center justify-center hover:bg-red-600 transition-colors"
              >
                <X className="w-3.5 h-3.5 text-white" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-40 h-40 rounded-xl border-2 border-dashed border-white/[0.12] hover:border-blue-500/40 hover:bg-blue-500/5 flex flex-col items-center justify-center gap-2 text-slate-500 hover:text-blue-400 transition-colors"
            >
              <ImageIcon className="w-8 h-8" />
              <span className="text-xs text-center leading-tight">{t('adminNotifications.modalForm.addPhoto')}</span>
            </button>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {t('adminNotifications.modalForm.linkLabel')}
          </label>
          <div className="relative">
            <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="url"
              placeholder={t('adminNotifications.modalForm.linkPlaceholder')}
              value={link}
              onChange={(e) => setLink(e.target.value)}
              className="w-full bg-[#0d1424] border border-white/[0.08] rounded-xl pl-9 pr-4 py-2.5 text-sm text-slate-300 placeholder-slate-600 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 transition-colors"
            />
          </div>
        </div>

        <div className="rounded-2xl border border-white/[0.08] bg-[#0d1424] p-4 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-white">{t('adminNotifications.modalForm.scheduleSectionTitle')}</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {t('adminNotifications.modalForm.scheduleSectionHint')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setScheduleEnabled((v) => !v)}
              className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${
                scheduleEnabled ? 'bg-blue-600' : 'bg-[#1e2d47]'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                  scheduleEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {scheduleEnabled && (
            <div className="space-y-5 pt-1">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
                  {t('adminNotifications.modalForm.showTimeLabel')}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1.5">{t('adminNotifications.modalForm.dateLabel')}</label>
                    <input
                      type="date"
                      value={showDate}
                      onChange={(e) => setShowDate(e.target.value)}
                      className="w-full bg-[#161f31] border border-white/[0.08] rounded-xl px-3 py-2.5 text-sm text-slate-300 focus:outline-none focus:border-blue-500/50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1.5">{t('adminNotifications.modalForm.startTimeLabel')}</label>
                    <input
                      type="time"
                      value={windowStartTime}
                      onChange={(e) => setWindowStartTime(e.target.value)}
                      className="w-full bg-[#161f31] border border-white/[0.08] rounded-xl px-3 py-2.5 text-sm text-slate-300 focus:outline-none focus:border-blue-500/50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1.5">{t('adminNotifications.modalForm.endTimeLabel')}</label>
                    <input
                      type="time"
                      value={windowEndTime}
                      onChange={(e) => setWindowEndTime(e.target.value)}
                      className="w-full bg-[#161f31] border border-white/[0.08] rounded-xl px-3 py-2.5 text-sm text-slate-300 focus:outline-none focus:border-blue-500/50"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-slate-600 mt-2">
                  {t('adminNotifications.modalForm.showTimeHint')}
                </p>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
                  {t('adminNotifications.modalForm.durationSectionTitle')}
                </p>
                <div className="grid grid-cols-2 gap-3 max-w-sm">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1.5">{t('adminNotifications.modalForm.daysLabel')}</label>
                    <input
                      type="number"
                      min="0"
                      value={durationDays}
                      onChange={(e) => setDurationDays(Math.max(0, Number(e.target.value) || 0))}
                      className="w-full bg-[#161f31] border border-white/[0.08] rounded-xl px-3 py-2.5 text-sm text-slate-300 focus:outline-none focus:border-blue-500/50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1.5">{t('adminNotifications.modalForm.hoursLabel')}</label>
                    <input
                      type="number"
                      min="0"
                      value={durationHours}
                      onChange={(e) => setDurationHours(Math.max(0, Number(e.target.value) || 0))}
                      className="w-full bg-[#161f31] border border-white/[0.08] rounded-xl px-3 py-2.5 text-sm text-slate-300 focus:outline-none focus:border-blue-500/50"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-slate-600 mt-2">
                  {t('adminNotifications.modalForm.durationHint')}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            onClick={handleClear}
            className="px-5 py-2.5 rounded-xl border border-white/[0.08] text-sm text-slate-400 hover:bg-white/[0.04] hover:text-slate-300 transition-colors"
          >
            {t('adminNotifications.modalForm.clear')}
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={selectedCount === 0 || sending}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-medium transition-all ${
              selectedCount > 0 && !sending
                ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/20'
                : 'bg-[#1a2640] text-slate-600 cursor-not-allowed'
            }`}
          >
            {sending ? (
              <><Loader className="w-4 h-4 animate-spin" />{t('adminNotifications.modalForm.sending')}</>
            ) : sent ? (
              <><CheckCircle2 className="w-4 h-4 text-green-400" />{t('adminNotifications.modalForm.sent')}</>
            ) : (
              <><MonitorPlay className="w-4 h-4" />{t('adminNotifications.modalForm.sendButton', { count: selectedCount })}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Zengin metin editörü ─────────────────────────────────── */
const RichTextEditor = ({ onChange }) => {
  const { t } = useTranslation('admin');
  const editorRef = useRef(null);
  const [showLink, setShowLink] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const savedSelRef = useRef(null);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) savedSelRef.current = sel.getRangeAt(0).cloneRange();
  };

  const restoreSelection = () => {
    if (!savedSelRef.current) return;
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(savedSelRef.current);
  };

  const exec = (cmd) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, null);
    onChange(editorRef.current?.innerHTML || '');
  };

  const handleLinkInsert = () => {
    restoreSelection();
    const url = /^https?:\/\//i.test(linkUrl) ? linkUrl : `https://${linkUrl}`;
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
      document.execCommand('createLink', false, url);
    } else {
      const a = document.createElement('a');
      a.href = url;
      a.textContent = linkText || linkUrl;
      const range = savedSelRef.current || document.createRange();
      range.collapse(false);
      range.insertNode(a);
      // Cursor'u linkin sonuna taşı
      range.setStartAfter(a);
      range.setEndAfter(a);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
    onChange(editorRef.current?.innerHTML || '');
    setShowLink(false);
    setLinkUrl('');
    setLinkText('');
  };

  const toolbarBtn = 'px-2.5 py-1 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.07] transition-colors text-xs select-none';

  return (
    <div className="border border-white/[0.08] rounded-xl overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-3 py-2 border-b border-white/[0.06] bg-[#0a1020]">
        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec('removeFormat'); }} className={toolbarBtn} title={t('adminNotifications.richTextEditor.normal')}>
          N
        </button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec('bold'); }} className={`${toolbarBtn} font-bold`} title={t('adminNotifications.richTextEditor.bold')}>
          B
        </button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec('italic'); }} className={`${toolbarBtn} italic`} title={t('adminNotifications.richTextEditor.italic')}>
          I
        </button>
        <div className="w-px h-4 bg-white/[0.08] mx-1" />
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); saveSelection(); setShowLink(true); }}
          className={`${toolbarBtn} flex items-center gap-1`}
          title={t('adminNotifications.richTextEditor.addLink')}
        >
          <Link2 className="w-3 h-3" /> Link
        </button>
      </div>

      {/* Link giriş alanı */}
      {showLink && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-white/[0.06] bg-[#0a1020]">
          <input
            autoFocus
            type="text"
            placeholder={t('adminNotifications.richTextEditor.linkPlaceholder')}
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleLinkInsert(); if (e.key === 'Escape') setShowLink(false); }}
            className="flex-1 bg-[#1a2640] text-xs text-slate-300 placeholder-slate-600 px-2.5 py-1.5 rounded-lg border border-white/[0.08] focus:outline-none focus:border-blue-500/40 min-w-0"
          />
          <input
            type="text"
            placeholder={t('adminNotifications.richTextEditor.linkTextPlaceholder')}
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleLinkInsert(); if (e.key === 'Escape') setShowLink(false); }}
            className="flex-1 bg-[#1a2640] text-xs text-slate-300 placeholder-slate-600 px-2.5 py-1.5 rounded-lg border border-white/[0.08] focus:outline-none focus:border-blue-500/40 min-w-0"
          />
          <button type="button" onClick={handleLinkInsert} className="text-xs text-blue-400 hover:text-blue-300 font-medium px-1.5">{t('adminNotifications.richTextEditor.add')}</button>
          <button type="button" onClick={() => setShowLink(false)} className="text-slate-500 hover:text-slate-300"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {/* Yazı alanı */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(editorRef.current?.innerHTML || '')}
        className="min-h-[120px] px-4 py-3 text-sm text-slate-300 focus:outline-none leading-relaxed empty:before:content-[attr(data-placeholder)] empty:before:text-slate-600"
        data-placeholder={t('adminNotifications.richTextEditor.textPlaceholder')}
        style={{ wordBreak: 'break-word' }}
      />
    </div>
  );
};

/* ─── Çan Bildirimi Formu ──────────────────────────────────── */
const BellNotificationForm = ({ onBack }) => {
  const { t } = useTranslation('admin');
  const { authToken, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selected, setSelected] = useState('all');
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await adminApi.getUsers({ token: authToken, userId: user?.id });
        setUsers(res.users || []);
      } catch {
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    load();
  }, [authToken, user]);

  const selectedCount = selected === 'all' ? users.length : (Array.isArray(selected) ? selected.length : 0);

  const handleClear = () => {
    setSelected('all');
    setContent('');
  };

  const handleSend = async () => {
    if (selectedCount === 0 || !content.trim()) return;
    setSending(true);
    try {
      await request('/admin/notifications/send', {
        method: 'POST',
        token: authToken,
        body: {
          type: 'bell',
          recipientType: selected === 'all' ? 'all' : 'special',
          recipientIds: selected === 'all' ? null : selected,
          content,
        },
      });
      setSent(true);
      setTimeout(() => setSent(false), 3000);
      handleClear();
    } catch {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'error', title: t('adminNotifications.toasts.errorTitle'), message: t('adminNotifications.toasts.sendFailed') },
      }));
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-8">
        <button onClick={onBack} className="p-2 rounded-xl hover:bg-white/[0.05] text-slate-400 transition-colors">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="w-9 h-9 rounded-xl bg-amber-500/10 flex items-center justify-center">
          <Bell className="w-5 h-5 text-amber-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-white">{t('adminNotifications.bellForm.title')}</h1>
          <p className="text-xs text-slate-500">{t('adminNotifications.bellForm.subtitle')}</p>
        </div>
      </div>

      <div className="space-y-6 max-w-2xl">
        {/* Alıcılar */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">{t('adminNotifications.bellForm.recipientsLabel')}</label>
          {loadingUsers ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm py-4">
              <Loader className="w-4 h-4 animate-spin" /><span>{t('adminNotifications.bellForm.loadingUsers')}</span>
            </div>
          ) : (
            <UserSelector users={users} selected={selected} onChange={setSelected} />
          )}
          {selectedCount > 0 && (
            <p className="text-xs text-amber-400 mt-2">{t('adminNotifications.bellForm.selectedCount', { count: selectedCount })}</p>
          )}
        </div>

        {/* Metin editörü */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">{t('adminNotifications.bellForm.textLabel')}</label>
          <RichTextEditor onChange={setContent} />
          {!content.trim() && (
            <p className="text-xs text-slate-600 mt-1.5">{t('adminNotifications.bellForm.textRequired')}</p>
          )}
        </div>

        {/* Butonlar */}
        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            onClick={handleClear}
            className="px-5 py-2.5 rounded-xl border border-white/[0.08] text-sm text-slate-400 hover:bg-white/[0.04] hover:text-slate-300 transition-colors"
          >
            {t('adminNotifications.bellForm.clear')}
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={selectedCount === 0 || !content.trim() || sending}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-medium transition-all ${
              selectedCount > 0 && content.trim() && !sending
                ? 'bg-amber-500 hover:bg-amber-400 text-white shadow-lg shadow-amber-500/20'
                : 'bg-[#1a2640] text-slate-600 cursor-not-allowed'
            }`}
          >
            {sending ? (
              <><Loader className="w-4 h-4 animate-spin" />{t('adminNotifications.bellForm.sending')}</>
            ) : sent ? (
              <><CheckCircle2 className="w-4 h-4 text-green-400" />{t('adminNotifications.bellForm.sent')}</>
            ) : (
              <><Bell className="w-4 h-4" />{t('adminNotifications.bellForm.sendButton', { count: selectedCount })}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Kart listesi sabiti ───────────────────────────────────── */
const getCards = (t) => [
  { id: 'modal', label: t('adminNotifications.cards.modal.label'), description: t('adminNotifications.cards.modal.description'), icon: MonitorPlay, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  { id: 'bell', label: t('adminNotifications.cards.bell.label'), description: t('adminNotifications.cards.bell.description'), icon: Bell, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  { id: 'history', label: t('adminNotifications.cards.history.label'), description: t('adminNotifications.cards.history.description'), icon: History, color: 'text-purple-400', bg: 'bg-purple-500/10' },
];

/* ─── Ana bileşen ──────────────────────────────────────────── */
const AdminNotifications = () => {
  const { t } = useTranslation('admin');
  const CARDS = getCards(t);
  const [view, setView] = useState('list');

  if (view === 'modal') return <ModalNotificationForm onBack={() => setView('list')} />;
  if (view === 'history') return <NotificationHistory onBack={() => setView('list')} />;
  if (view === 'bell') return <BellNotificationForm onBack={() => setView('list')} />;

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{t('adminNotifications.mainTitle')}</h1>
      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 mb-8">{t('adminNotifications.mainSubtitle')}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {CARDS.slice(0, 2).map((card) => {
          const Icon = card.icon;
          return (
            <button
              key={card.id}
              onClick={() => setView(card.id)}
              className="bg-white dark:bg-[#0d1424] border border-slate-200 dark:border-white/[0.08] rounded-2xl p-6 text-left hover:border-white/20 hover:bg-[#111827] transition-all group"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-xl ${card.bg} flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${card.color}`} />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-slate-900 dark:text-white group-hover:text-white transition-colors">{card.label}</h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{card.description}</p>
                </div>
              </div>
              <p className={`text-xs font-medium ${card.color} opacity-0 group-hover:opacity-100 transition-opacity`}>{t('adminNotifications.clickToContinue')}</p>
            </button>
          );
        })}
      </div>

      <div className="mt-6">
        {CARDS.slice(2).map((card) => {
          const Icon = card.icon;
          return (
            <button
              key={card.id}
              onClick={() => setView(card.id)}
              className="w-full bg-white dark:bg-[#0d1424] border border-slate-200 dark:border-white/[0.08] rounded-2xl p-6 text-left hover:border-white/20 hover:bg-[#111827] transition-all group flex items-center gap-4"
            >
              <div className={`w-10 h-10 rounded-xl ${card.bg} flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-5 h-5 ${card.color}`} />
              </div>
              <div>
                <h2 className="text-base font-semibold text-slate-900 dark:text-white group-hover:text-white transition-colors">{card.label}</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">{card.description}</p>
              </div>
              <span className={`ml-auto text-xs font-medium ${card.color} opacity-0 group-hover:opacity-100 transition-opacity`}>→</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AdminNotifications;
